import { ReactNode, useMemo, useRef } from 'react';

import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ApolloProvider } from '@apollo/client/react';

import { useAuthentication } from '@freelancelabs/components';

interface Props {
    children: ReactNode;
}

const ApiProvider = ({ children }: Props) => {
    const { getAuthorizationToken } = useAuthentication();

    // The provider hands out a new function on every render, so the link reads it
    // through a ref: the client below has to be built once, or its cache is thrown
    // away at each render.
    const authorizationToken = useRef(getAuthorizationToken);
    authorizationToken.current = getAuthorizationToken;

    const client = useMemo(() => {
        const httpLink = new HttpLink({
            uri: import.meta.env.PUBLIC_CLIENT_API_URL,
        });

        const authLink = setContext(async (_, { headers }) => {
            const token = await authorizationToken.current();
            return {
                headers: {
                    ...headers,
                    authorization: token ? `Bearer ${token}` : '',
                },
            };
        });

        return new ApolloClient({
            link: authLink.concat(httpLink),
            // No type policy yet: the Client Hub schema paginates with
            // limit/offset, it exposes no Relay connection to normalise.
            cache: new InMemoryCache(),
        });
    }, []);

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApiProvider;
